import { useSocialLinks } from 'app/hooks/use-social-links';
import type { UseMutationOptions } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getBffQueryData, useQueryLPC } from 'app/lib/query';
import { http } from 'app/lib/axios';
import type { FunctionalUseQueryOptions } from 'app/types/network';
import { useAtomValue } from 'jotai';
import { bffUserProfileLoadedAtom, isInsideOnboardingAtom, isLoggedInAtom, userInfoAtom } from 'app/atoms';
import { groupKeys } from 'app/network/keys';
import type { Group } from 'app/types/group';
import type { GroupByTypeSummary, GroupType } from 'app/types/group-base';
import { GroupType as GroupTypeValue, isTrustCircleGroup } from 'app/types/group-base';
import { WhiteLabel } from 'app/types/white-labels';
import { FeatureFlag, isFeatureEnabled, isWhiteLabel } from 'app/utils/helpers';

const activitySectorFeatureFlag = FeatureFlag.useActivitySector;

export interface ActivitySector {
  id: string;
  name: string;
  type: 'sector';
  subtype: string;
  picture: string | null;
  slug: string | null;
  selected: boolean;
}

export function useAllGroups(excludeMunicipal = false, opts: FunctionalUseQueryOptions<Group[]> = {}) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const client = useQueryClient();
  const bffUserProfileLoaded = useAtomValue(bffUserProfileLoadedAtom);
  const isOnboarding = useAtomValue(isInsideOnboardingAtom);

  const queryKey = groupKeys.list('all');
  return useQuery<Group[]>({
    queryKey: [...queryKey, excludeMunicipal],
    queryFn: async () => {
      const resData =
        (bffUserProfileLoaded && !isOnboarding ? getBffQueryData<Group[]>(client, queryKey) : undefined) ??
        (await http.get<Group[]>('/groups')).data;

      resData.forEach((group) => {
        client.setQueryData(groupKeys.detail(group.id), group);
      });

      return resData.filter(!excludeMunicipal ? Boolean : (group) => group.subtype !== 'municipal');
    },
    ...opts,
    enabled: isLoggedIn && (opts.enabled ?? true) && bffUserProfileLoaded,
  });
}

export function useGroupsByType(type: GroupType, opts: FunctionalUseQueryOptions<GroupByTypeSummary[]> = {}) {
  return useQueryLPC<GroupByTypeSummary[]>({
    queryKey: [...groupKeys.list('byType'), type],
    queryFn: () => http.get<GroupByTypeSummary[]>('/groups/by_type', { params: { type } }).then((res) => res.data),
    ...opts,
    enabled: opts.enabled ?? true,
  });
}

/**
 * Groups that can be selected while publishing a listing.
 *
 * Haute-Gaspésie listings can target any sector, regardless of the publisher's
 * memberships. Using only sector groups also omits the universal MRC partner club.
 */
export function useListingVisibilityGroups(includeAllSectorGroups = false, enabled = true) {
  const socialLinks = useSocialLinks();
  const viewer = useAtomValue(userInfoAtom);
  const useAllSectorGroups = includeAllSectorGroups && isWhiteLabel(WhiteLabel.HauteGaspesie);
  const memberGroups = useAllGroups(true, { enabled: enabled && !useAllSectorGroups });
  const sectorGroups = useGroupsByType(GroupTypeValue.sector, { enabled: enabled && useAllSectorGroups });

  const query = useAllSectorGroups ? sectorGroups : memberGroups;
  return {
    ...query,
    data: query.data?.filter(
      (group) => !isTrustCircleGroup(group) || (socialLinks.enabled && group.ownerId === viewer?.id),
    ),
  };
}

export function useActivitySectors(opts: FunctionalUseQueryOptions<ActivitySector[]> = {}) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const userInfo = useAtomValue(userInfoAtom);
  const enabledByFlag = isFeatureEnabled(activitySectorFeatureFlag, userInfo?.featureFlags);
  return useQuery<ActivitySector[]>({
    queryKey: groupKeys.activitySectors(),
    queryFn: async () => {
      const { data } = await http.get<{ sectors: ActivitySector[] }>('/groups/activity-sectors');
      return data.sectors;
    },
    ...opts,
    enabled: (opts.enabled ?? true) && isLoggedIn && enabledByFlag,
    staleTime: 5 * 60 * 1000,
  });
}

export function useToggleActivitySector(
  opts: UseMutationOptions<{ joined: boolean; removed: boolean }, Error, { groupId: string; selected: boolean }> = {},
) {
  const client = useQueryClient();
  return useMutation<{ joined: boolean; removed: boolean }, Error, { groupId: string; selected: boolean }>({
    mutationFn: async ({ groupId, selected }) => {
      if (selected) {
        await http.delete(`/groups/activity-sectors/${groupId}/leave`);
        return { joined: false, removed: true };
      }
      await http.post(`/groups/activity-sectors/${groupId}/join`);
      return { joined: true, removed: false };
    },
    ...opts,
    async onMutate({ groupId, selected }) {
      await client.cancelQueries({ queryKey: groupKeys.activitySectors() });
      const previous = client.getQueryData<ActivitySector[]>(groupKeys.activitySectors());
      if (previous) {
        client.setQueryData<ActivitySector[]>(groupKeys.activitySectors(), (old) =>
          (old ?? []).map((s) => (s.id === groupId ? { ...s, selected: !selected } : s)),
        );
      }
      return { previous };
    },
    onError(_err, _variables, context) {
      const { previous } = (context as { previous?: ActivitySector[] } | undefined) ?? {};
      if (previous) {
        client.setQueryData(groupKeys.activitySectors(), previous);
      }
      return opts.onError?.(_err, _variables, context);
    },
    async onSuccess(result, variables, context) {
      await Promise.all([
        client.invalidateQueries({ queryKey: groupKeys.activitySectors() }),
        client.invalidateQueries({ queryKey: groupKeys.lists() }),
      ]);
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export const groupNameWithPrefix = (groupName: string | undefined, language: string) => {
  if (!groupName) return groupName;
  if (groupName.startsWith('Ville') && language === 'fr') {
    return `la ${groupName}`;
  }
  return groupName;
};
